"use client";
import Link from "@/components/ui/Link";
import Image from "next/image";
import { useEffect } from "react";
import { X } from "lucide-react";
import { SIDEBAR_NAV } from "@/lib/navigation";
import { SITE } from "@/lib/seo/site";
import { cn } from "@/lib/utils";

interface SidebarNavProps {
  open: boolean;
  onClose: () => void;
}

export function SidebarNav({ open, onClose }: SidebarNavProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop mounted only while open. A permanent `fixed inset-0`
          overlay makes AdSense Auto Ads treat the whole viewport as
          covered and never place the side rails. */}
      {open && <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} aria-hidden />}
      <nav
        data-track-zone="sidebar"
        className={cn(
          // Anchored to the left edge only — never inset-0 — so when closed
          // (-translate-x-full parks it fully off-screen) it occupies zero
          // on-screen area and can't block Auto Ads rails.
          "fixed top-0 left-0 z-50 h-full w-70 max-w-[85vw] bg-white shadow-2xl transition-transform duration-200",
          open ? "translate-x-0" : "pointer-events-none -translate-x-full",
        )}
        aria-label="Site navigation"
        // `inert` (not aria-hidden) while closed: it parks the drawer
        // off-screen but its links stay in the DOM, so aria-hidden alone
        // would leave focusable descendants in the tab order — the
        // aria-hidden-focus accessibility failure. `inert` removes the
        // subtree from both the tab order and the accessibility tree.
        inert={!open}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <Link
            href="/"
            onClick={onClose}
            aria-label="Sticko home"
            className="relative inline-block h-10 w-10 overflow-hidden rounded-full"
          >
            {/* Conditional render — not just loading="lazy". The drawer is
                positioned `fixed inset-y-0` with a transform hiding it
                offscreen, so its layout box still overlaps the viewport
                and Chrome fetches lazy images inside it during initial
                page load. Mounting the <Image> only when `open` defers
                the ~640KB fetch until the user actually taps Menu. */}
            {open ? (
              <Image
                src={`${SITE.cdn}/static-content/sticko-side-nav-icon.svg`}
                alt="Sticko"
                fill
                sizes="40px"
                unoptimized
              />
            ) : null}
          </Link>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="text-ink hover:bg-muted -mr-2 rounded-full p-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="px-2 py-3">
          {SIDEBAR_NAV.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                onClick={onClose}
                className="text-foreground hover:bg-muted block rounded-lg px-4 py-3 text-[15px] font-medium"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
